import { memo, useState } from "react";
import { Download, X, FileText, Maximize2, Flag } from "lucide-react";
import lunaAvatar from "@/assets/luna-avatar.webp";
import Markdown from "@/components/Markdown";
import { ActionCards, AttachmentList, MessageFile, MessageImage, SourceChips } from "@/components/MessageExtras";
import Lightbox from "./Lightbox";
import { makeT, uiLang } from "../lib/i18n";
import { canReport } from "../lib/report";
import { dataUrlMime, downloadName } from "../lib/files";

// memo: a streaming reply updates the message list many times a second, but
// only its own bubble gets a new msg object (lib/streamedTurn.js) — the
// rest of the chat skips re-rendering.
//
// Luna's text is Markdown (components/Markdown.jsx: headings, lists, tables,
// code with a copy button, links that open in a new tab), also while it
// streams in; what you typed stays plain text. Under her reply: the
// pictures / files made in chat (msg.attachments — also a PDF, Word, Excel
// or deck asked for straight away), what she did in your account
// (msg.actions: a reminder card with "Takvime ekle" / ".ics indir", a note,
// a goal...) and the web pages a searched reply used (msg.sources).
// onReport(msg): her finished replies — a picture or a file too, and the
// turns of a voice call shown in the chat — end with a "Bildir" button that
// opens the report dialog (lib/report.js; pages/Luna.jsx). Always visible,
// not only on hover: a phone has no hover. A picture is saved as what it is
// (a JPEG as .jpg — lib/files.js downloadName). A big picture or file of a
// reloaded thread came without its bytes (msg.imageRef / msg.fileRef —
// lib/api.js historyFileRef): fetched when shown / downloaded
// (components/MessageExtras.jsx MessageImage, MessageFile).
function ChatMessage({ msg, lang = "tr", onReport = null }) {
  const isUser = msg.role === "user";
  // The picture open full screen: { src, name } | null.
  const [lightbox, setLightbox] = useState(null);
  // The interface language, "tr" or "en" (lib/i18n.js) — a "de" or "es"
  // screen reads English labels, never Turkish ones.
  const ui = uiLang(lang);
  const t = makeT(ui);

  const userBubble = "bg-gradient-to-br from-fuchsia-500/22 to-indigo-500/18 border-fuchsia-400/30 text-fuchsia-50";
  const lunaBubble = "bg-[#140b28]/90 border-purple-400/20 text-purple-50";

  return (
    <div className={`flex items-end gap-2 fade-up ${isUser ? "justify-end" : "justify-start"}`}
      data-testid={isUser ? "user-chat-bubble" : "luna-chat-bubble"}>
      {!isUser && (
        <img src={lunaAvatar} alt="Luna" data-testid="luna-avatar"
          className="shrink-0 w-7 h-7 rounded-full object-cover mb-0.5"
          style={{ boxShadow: "0 0 12px rgba(192,132,252,0.5)" }} />
      )}
      <div className={`min-w-0 border px-4 py-2.5 text-sm leading-relaxed backdrop-blur-md rounded-2xl rounded-bl-md ${
        isUser ? `max-w-[80%] ${userBubble} rounded-br-md` : `max-w-[88%] sm:max-w-[80%] ${lunaBubble}`
      }`}>
        {!isUser && (
          <div className="mb-1">
            <span className="text-[10px] uppercase tracking-[0.2em] text-purple-300/80">
              Luna
            </span>
          </div>
        )}
        {msg.imageUrl && (
          <button onClick={() => setLightbox({ src: msg.imageUrl, name: downloadName(`luna-${msg.id}`, dataUrlMime(msg.imageUrl), "png") })}
            data-testid="chat-image-attachment" aria-label={t("Görseli büyüt", "Open image")}
            className="block w-full mb-2 group relative">
            <img src={msg.imageUrl} alt=""
              className="w-full max-h-[28rem] rounded-xl object-contain transition-opacity group-hover:opacity-80" />
            <span className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/0 group-hover:bg-black/25 transition-colors opacity-0 group-hover:opacity-100">
              <Maximize2 size={22} className="text-white drop-shadow" />
            </span>
          </button>
        )}
        {!msg.imageUrl && msg.imageRef && <MessageImage fileRef={msg.imageRef} lang={ui} onOpen={setLightbox} />}
        {msg.fileUrl && (
          <a href={msg.fileUrl} download={msg.fileName} data-testid="chat-file-attachment"
            className="flex items-center gap-2.5 mb-2 rounded-xl border border-purple-400/25 bg-black/20 px-3.5 py-2.5 hover:bg-black/30 transition-colors">
            <FileText size={17} className="text-purple-300 shrink-0" />
            <span className="text-xs font-medium text-purple-100 truncate flex-1">{msg.fileName}</span>
            <Download size={15} className="text-purple-300 shrink-0" />
          </a>
        )}
        {!msg.fileUrl && msg.fileRef && <MessageFile fileRef={msg.fileRef} lang={ui} />}
        {msg.text && (isUser
          ? <p className="whitespace-pre-wrap break-words">{msg.text}</p>
          : <Markdown text={msg.text} streaming={Boolean(msg.streaming)} lang={ui} />)}
        {!isUser && <AttachmentList attachments={msg.attachments} lang={ui} onOpenImage={setLightbox} />}
        {!isUser && !msg.streaming && <ActionCards actions={msg.actions} lang={ui} />}
        {!isUser && !msg.streaming && <SourceChips sources={msg.sources} lang={ui} />}
        {onReport && canReport(msg) && (
          <div className="flex justify-end mt-1.5 -mb-1 -mr-1.5">
            <button type="button" onClick={() => onReport(msg)} data-testid="chat-report-button"
              aria-label={t("Bu mesajı bildir", "Report this message")}
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] text-purple-300/80 hover:text-purple-100 hover:bg-white/[0.06] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-300/60">
              <Flag size={11} aria-hidden="true" /> {t("Bildir", "Report")}
            </button>
          </div>
        )}
      </div>

      {lightbox && (
        // (a dialog portaled to <body> — components/Lightbox.jsx says why)
        <Lightbox label={t("Görsel", "Image")} onClose={() => setLightbox(null)} testId="chat-image-lightbox" zClass="z-[100]">
          <img src={lightbox.src} alt="" className="w-full rounded-2xl border border-white/10"
            style={{ maxHeight: "85vh", objectFit: "contain" }} />
          <div className="flex items-center gap-3">
            <a href={lightbox.src} download={lightbox.name} data-testid="chat-image-download-button"
              className="flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full border border-purple-400/30 text-purple-200 hover:bg-purple-400/15">
              <Download size={13} aria-hidden="true" /> {t("İndir", "Download")}
            </a>
            <button type="button" onClick={() => setLightbox(null)} data-testid="chat-image-close-button"
              className="flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full border border-white/15 text-white/60 hover:text-white hover:bg-white/5">
              <X size={13} aria-hidden="true" /> {t("Kapat", "Close")}
            </button>
          </div>
        </Lightbox>
      )}
    </div>
  );
}

export default memo(ChatMessage);
